import { z } from 'zod';

import {
  AdjustmentMode,
  BoundOperator,
  ProductPriceBasisType,
  QualityAdjustmentType,
  QualityFormulaType,
  RangeRowIssueType,
  SpecType,
  TierMode,
} from '../types';
import { PRICE_COMPONENTS } from '../utils/priceComponents';
import {
  findRangeRowIssues,
  type IBoundedFormulaRow,
  isFormulaTypeAllowedForSpecType,
  isQuantityFormulaType,
} from '../utils/qualityFormulaMatching';

const finiteNumber = z.number();
const nonZeroNumber = finiteNumber.refine(value => value !== 0, {
  message: 'Must not be zero',
});
const boundOperator = z.enum([
  BoundOperator.Exclusive,
  BoundOperator.Inclusive,
]);

const PRICE_COMPONENT_KEYS = new Set(
  PRICE_COMPONENTS.map(component => component.key),
);

/** One entry of a "Price Components" basis's `componentKeys` — must reference the shared catalog. */
const priceComponentKeySchema = z
  .string()
  .refine(key => PRICE_COMPONENT_KEYS.has(key), {
    message: 'Unknown price component key',
  });

const relativePercentageSpecTypes = new Set<string>([
  SpecType.Btu,
  SpecType.Hgi,
  SpecType.Kcal,
]);

/**
 * Rejects a zero `contractedSpec` for Percentage formulas on spec types whose adjustment is a
 * relative percentage of that baseline (division by zero is otherwise undefined) — shared by
 * both the persisted spec schema (`PercentageQualitySpecSchema`) and the calculate-endpoint
 * request schema so a template can never be saved with a value the calculate endpoint would
 * then reject.
 */
const rejectZeroContractedSpecForRelativePercentage = (
  specType: string,
  contractedSpec: number,
  context: z.RefinementCtx,
  path: Array<number | string>,
): void => {
  if (relativePercentageSpecTypes.has(specType) && contractedSpec === 0) {
    context.addIssue({
      code: 'custom',
      message:
        'The contracted specification must not be zero for relative percentage formulas',
      path,
    });
  }
};

/**
 * Rejects a `specType`/`formulaType` combination the UI would never let a user build — shared
 * by the persisted spec schema and the calculate-endpoint request schema so a template can never
 * be saved with a combination the calculate endpoint would then reject (see
 * `isFormulaTypeAllowedForSpecType`).
 */
const rejectDisallowedFormulaTypeForSpecType = (
  specType: string,
  formulaType: QualityFormulaType,
  context: z.RefinementCtx,
  path: Array<number | string>,
): void => {
  if (!isFormulaTypeAllowedForSpecType(specType, formulaType)) {
    context.addIssue({
      code: 'custom',
      message: `${formulaType} is not a valid formula type for spec type ${specType}`,
      path,
    });
  }
};

// ── Product price basis (PERCENTAGE formula types only) ──────────────────────
// What a "% of Price" formula's percentFactor scales against. Optional on every Percentage
// spec/input — omitting it (or sending ContractPrice) preserves the long-standing behavior of
// scaling against the request's shared, top-level `tradePrice`. Not applicable to any other
// formula type: PerUnit/AdjustedSalesPrice/Quantity* formulas never scale by a price basis, so
// this is only ever added to the Percentage variants below, not the shared row/threshold shapes
// PerUnit also uses.
export const ProductPriceBasisSchema = z.discriminatedUnion('type', [
  /** Scales against the request's full calculated contract price — today's default behavior. */
  z.object({ type: z.literal(ProductPriceBasisType.ContractPrice) }),

  /**
   * Scales against one or more named components of the contract pricing formula (e.g. Base/Index
   * Price, Freight, Discounts, Premiums) instead of the full price. `componentKeys` references
   * entries in the shared `PRICE_COMPONENTS` catalog (`utils/priceComponents.ts`) by key rather
   * than a fixed enum, so the catalog — not this schema — is the single place that changes if the
   * pricing formula engine ever exposes a different/larger set of components. See
   * `resolvePriceComponentsTotal` for how `componentKeys` resolves to a total.
   */
  z.object({
    componentKeys: z.array(priceComponentKeySchema).min(1),
    type: z.literal(ProductPriceBasisType.PriceComponents),
  }),
  /** Overrides the calculation basis entirely with a specific negotiated price. */
  z.object({
    deemedPrice: finiteNumber.positive(),
    type: z.literal(ProductPriceBasisType.DeemedPrice),
  }),
]);

export type IProductPriceBasis = z.infer<typeof ProductPriceBasisSchema>;

// ── Formula row schemas ──────────────────────────────────────────────────────
// Each formula type has its own strict row shape — a row never carries fields
// that its formula doesn't use (e.g. a PerUnit row has no percentFactor).
//
// Percentage/PerUnit rows only select which tier applies (via lowerBound/upperBound
// and their operators) — the contracted spec every tier is measured against lives once on the
// enclosing spec/input instead (see `contractedSpec` on `PercentageQualitySpecSchema` etc.), so a
// multi-row schedule's tiers all share the same baseline rather than each defining their own.
//
// These "Range" row schemas are only used when `tierMode` is `Range` (an ordered chain of
// explicit, independently-bounded intervals). `Threshold` mode (1-2 rows anchored to the shared
// contracted spec) uses the distinct, bound-free `*ThresholdSideSchema`s further below instead —
// both variants expose their rows under the same `formulaRows` field name; see the
// `tierMode`-discriminated spec/input schemas for how the two are wired together.

/**
 * Fields shared by every Range row regardless of which rate field (percentFactor vs
 * qualityPricePerUnit) it carries — see PercentageFormulaRowSchema/PerUnitFormulaRowSchema below.
 */
const rangeRowBaseShape = {
  /** Which side of the contracted spec this row adjusts. */
  adjustmentMode: z.enum([AdjustmentMode.Discount, AdjustmentMode.Premium]),

  /**
   * Applies the rate as-is once this row matches (`true`), skipping the diff/perIncrement
   * scaling below, or applies the scaled behavior (`false`) — required for Range rows since
   * there is no long-standing default to fall back on.
   */
  isFlatAdjustment: z.boolean(),
  /** Row-selection floor. */
  lowerBound: finiteNumber,

  /**
   * Whether `lowerBound` itself counts as a match — omitted keeps the long-standing
   * default (`Exclusive`, i.e. `actualSpec > lowerBound`).
   */
  lowerBoundOperator: boundOperator,

  /**
   * Divides the scaled adjustment to express it "per contract increment" — ignored entirely by
   * a flat row. Omit for the common case of 1.
   */
  perIncrement: finiteNumber.positive().default(1),
  /** Row-selection ceiling, or `null` for no ceiling. */
  upperBound: finiteNumber.nullable(),

  /**
   * Whether `upperBound` itself counts as a match — omitted keeps the
   * long-standing default (`Inclusive`, i.e. `actualSpec <= upperBound`).
   */
  upperBoundOperator: boundOperator.optional(),
};

/**
 * One row of a `Percentage`-formula Range spec (used by `calculatePricePercentageBase`).
 * Multiple rows express an ordered chain of value ranges (e.g. HGI-style schedules).
 */
export const PercentageFormulaRowSchema = z.object({
  ...rangeRowBaseShape,

  /**
   * Multiplier applied to the normalized percent diff before scaling by tradePrice — or, for a
   * flat (`isFlatAdjustment`) row, the plain percent of tradePrice applied directly. Never zero
   * — a zero-rate row is a no-op, not a tier.
   */
  percentFactor: nonZeroNumber,
});

export type IPercentageFormulaRow = z.infer<typeof PercentageFormulaRowSchema>;

/**
 * One row of a `PerUnit`-formula spec (used by `calculatePricePerUnitBase`).
 * Multiple rows express tiered/multi-range formulas.
 */
export const PerUnitFormulaRowSchema = z.object({
  ...rangeRowBaseShape,

  /** Flat $-per-spec-unit rate applied to the diff. Never zero — a zero-rate row is a no-op. */
  qualityPricePerUnit: nonZeroNumber,
});

export type IPerUnitFormulaRow = z.infer<typeof PerUnitFormulaRowSchema>;

// ── Threshold side schemas ───────────────────────────────────────────────────
// Only used when `tierMode` is `Threshold`. A side has no bound fields at all — which side of
// the shared `contractedSpec` it applies to is fully derived server-side from `adjustmentMode`
// vs. the spec type's natural higher-value effect (`getHigherSpecEffect`), so callers never
// compute or send a bound for it (contrast with Range rows above).

/**
 * Fields shared by every Threshold side regardless of which rate field it carries — see
 * PercentageThresholdSideSchema/PerUnitThresholdSideSchema below.
 */
const thresholdSideBaseShape = {
  /** Which side of the contracted spec this side adjusts. */
  adjustmentMode: z.enum([AdjustmentMode.Discount, AdjustmentMode.Premium]),

  /**
   * Applies the rate as-is once this side applies, skipping the diff/perIncrement scaling
   * below — omitted (or `false`) preserves today's scaled behavior.
   */
  isFlatAdjustment: z.boolean().optional(),

  /**
   * Divides the scaled adjustment to express it "per contract increment" — ignored entirely by
   * a flat side. Omit for the common case of 1.
   */
  perIncrement: finiteNumber.positive().default(1),
};

/** One side of a `Percentage`-formula Threshold spec — just its rate, no bounds. */
export const PercentageThresholdSideSchema = z.object({
  ...thresholdSideBaseShape,

  /**
   * Multiplier applied to the normalized percent diff before scaling by tradePrice — or, for a
   * flat (`isFlatAdjustment`) side, the plain percent of tradePrice applied directly. Never zero
   * — a zero-rate side is a no-op, not a side.
   */
  percentFactor: nonZeroNumber,
});

export type IPercentageThresholdSide = z.infer<
  typeof PercentageThresholdSideSchema
>;

/** One side of a `PerUnit`-formula Threshold spec — just its rate, no bounds. */
export const PerUnitThresholdSideSchema = z.object({
  ...thresholdSideBaseShape,

  /** Flat $-per-spec-unit rate applied to the diff. Never zero — a zero-rate side is a no-op. */
  qualityPricePerUnit: nonZeroNumber,
});

export type IPerUnitThresholdSide = z.infer<typeof PerUnitThresholdSideSchema>;

/**
 * A Threshold spec/input carries at most one row per adjustmentMode — two Discount rows (or
 * two Premium rows) would be ambiguous about which one applies.
 */
const hasUniqueAdjustmentModes = (
  rows: Array<{ adjustmentMode: AdjustmentMode }>,
): boolean => new Set(rows.map(row => row.adjustmentMode)).size === rows.length;

/**
 * Shared shape for a Threshold-tier Percentage/PerUnit spec or input — 1-2 bound-free rows/sides
 * sharing `contractedSpec`, discriminated by `formulaType` and `tierMode`. Used to build both the
 * persisted spec and calculate-endpoint input variants below, which are otherwise identical here.
 */
const buildThresholdVariantShape = <
  RowSchema extends
    typeof PercentageThresholdSideSchema | typeof PerUnitThresholdSideSchema,
  Formula extends QualityFormulaType,
>(
  rowSchema: RowSchema,
  formulaType: Formula,
) => ({
  /** The value every row is measured against. */
  contractedSpec: finiteNumber,
  /** 1-2 rows, at most one per `adjustmentMode`. */
  formulaRows: z
    .array(rowSchema)
    .min(1)
    .max(2)
    .refine(hasUniqueAdjustmentModes, {
      message: 'formulaRows must not repeat the same adjustmentMode',
    }),
  /** Discriminant — always `formulaType` for this variant. */
  formulaType: z.literal(formulaType),
  /** Discriminant — always `Threshold` for this variant. */
  tierMode: z.literal(TierMode.Threshold),
});

/**
 * A Range spec/input's rows must land in ascending `lowerBound` order with no two rows claiming
 * the same value — otherwise `findApplicableRowIndex`'s first-match scan (see that function's own
 * doc comment) would silently resolve an ambiguous configuration by array position alone instead
 * of rejecting it. A `gap` (no row covers some interval) is left alone — that's valid, intentional
 * "no adjustment applies" business data, not an ambiguity — see `findRangeRowIssues`.
 */
const createRangeFormulaRowsSchema = <
  RowSchema extends
    typeof PercentageFormulaRowSchema | typeof PerUnitFormulaRowSchema,
>(
  rowSchema: RowSchema,
) =>
  z
    .array(rowSchema)
    .min(1)
    .superRefine((rows: IBoundedFormulaRow[], context) => {
      findRangeRowIssues(rows)
        .filter(issue => issue.type !== RangeRowIssueType.Gap)
        .forEach(issue => {
          context.addIssue({
            code: 'custom',
            message:
              issue.type === RangeRowIssueType.Unsorted
                ? `formulaRows[${issue.rowIndex + 1}] must not start before formulaRows[${issue.rowIndex}]`
                : `formulaRows[${issue.rowIndex}] and formulaRows[${issue.rowIndex + 1}] overlap`,
            path: [issue.rowIndex + 1, 'lowerBound'],
          });
        });
    });

/**
 * Shared shape for a Range-tier Percentage/PerUnit spec or input — an ordered chain of
 * independently-bounded rows using `contractedSpec` as their shared diff baseline. Used to build
 * both the persisted spec and calculate-endpoint input variants below.
 */
const buildRangeVariantShape = <
  RowSchema extends
    typeof PercentageFormulaRowSchema | typeof PerUnitFormulaRowSchema,
  Formula extends QualityFormulaType,
>(
  rowSchema: RowSchema,
  formulaType: Formula,
) => ({
  /** The value every row's diff is measured against; row bounds only select which row applies. */
  contractedSpec: finiteNumber,
  /** One or more ordered, non-overlapping rows — see `createRangeFormulaRowsSchema`. */
  formulaRows: createRangeFormulaRowsSchema(rowSchema),
  /** Discriminant — always `formulaType` for this variant. */
  formulaType: z.literal(formulaType),
  /** Discriminant — always `Range` for this variant. */
  tierMode: z.literal(TierMode.Range),
});

/**
 * The single, unconditional row of an `AdjustedSalesPrice`-formula spec (used by
 * `calculateAdjustedPriceToContracted`). No discount/premium split — it always applies.
 */
export const AdjustedSalesPriceFormulaRowSchema = z.object({
  /** The contracted spec used directly in the adjusted-sales-price ratio. */
  contractedSpec: finiteNumber.refine(value => value !== 100, {
    message: 'ContractedSpec must not be 100 for adjusted sales price formulas',
  }),
});

export type IAdjustedSalesPriceFormulaRow = z.infer<
  typeof AdjustedSalesPriceFormulaRowSchema
>;

/**
 * The single, unconditional row of a quantity-formula spec (`QuantityAdjustedToContracted`
 * or `QuantityAdjustedToDry`). Only adjusts when `actualSpec` exceeds the contracted spec
 * — excess penalizes the invoice quantity, a deficit is never a bonus.
 */
export const QuantityFormulaRowSchema = z.object({
  /** The contracted spec — the row only applies when `actualSpec > lowerBound`. */
  lowerBound: finiteNumber,
});

export type IQuantityFormulaRow = z.infer<typeof QuantityFormulaRowSchema>;

// ── Quality spec schema (persisted contract formula configuration) ──────────
// Array-based and keyed by an explicit `specType` string — stored per contract
// line item (`ContractDto.ts`), independent of the calculate endpoint below.

const QualitySpecBaseSchema = z.object({
  /** Rounding precision (decimal places) applied to the calculated adjustment. */
  precision: z.number().int().min(0).max(10).default(5),
  /** The quality attribute this spec configures formula behavior for. */
  specType: z.string(),
});

// Percentage/PerUnit specs carry a second discriminant, `tierMode`, because Threshold and Range
// are genuinely different request shapes rather than two ways of filling in the same one —
// Threshold's 1-2 rows have no bounds at all (fully derived from `contractedSpec`), while
// Range's rows are independently-bounded and carry no shared `contractedSpec`-derived bound.
// Both share `contractedSpec` itself: Threshold's rows are measured against it directly, and
// Range's rows use it as their diff baseline (their own bounds only select *which* row applies).
// Both variants expose their rows under the same `formulaRows` field name.

/** Percentage-formula Threshold spec — one guaranteed value, 1-2 bound-free rows. */
export const PercentageThresholdQualitySpecSchema =
  QualitySpecBaseSchema.extend({
    ...buildThresholdVariantShape(
      PercentageThresholdSideSchema,
      QualityFormulaType.Percentage,
    ),
    /** What this spec's percentFactor scales against — see ProductPriceBasisSchema. */
    productPriceBasis: ProductPriceBasisSchema.optional(),
  });

/** Percentage-formula Range spec — an ordered chain of independently-bounded rows. */
export const PercentageRangeQualitySpecSchema = QualitySpecBaseSchema.extend({
  ...buildRangeVariantShape(
    PercentageFormulaRowSchema,
    QualityFormulaType.Percentage,
  ),
  /** What this spec's percentFactor scales against — see ProductPriceBasisSchema. */
  productPriceBasis: ProductPriceBasisSchema.optional(),
});

/** Percentage-formula spec — formula configuration for one contract-line quality attribute. */
export const PercentageQualitySpecSchema = z
  .discriminatedUnion('tierMode', [
    PercentageThresholdQualitySpecSchema,
    PercentageRangeQualitySpecSchema,
  ])
  .superRefine((spec, context) => {
    rejectZeroContractedSpecForRelativePercentage(
      spec.specType,
      spec.contractedSpec,
      context,
      ['contractedSpec'],
    );
  });

/** PerUnit-formula Threshold spec — one guaranteed value, 1-2 bound-free rows. */
export const PerUnitThresholdQualitySpecSchema = QualitySpecBaseSchema.extend(
  buildThresholdVariantShape(
    PerUnitThresholdSideSchema,
    QualityFormulaType.PerUnit,
  ),
);

/** PerUnit-formula Range spec — an ordered chain of independently-bounded rows. */
export const PerUnitRangeQualitySpecSchema = QualitySpecBaseSchema.extend(
  buildRangeVariantShape(PerUnitFormulaRowSchema, QualityFormulaType.PerUnit),
);

/** PerUnit-formula spec — formula configuration for one contract-line quality attribute. */
export const PerUnitQualitySpecSchema = z.discriminatedUnion('tierMode', [
  PerUnitThresholdQualitySpecSchema,
  PerUnitRangeQualitySpecSchema,
]);

/** AdjustedSalesPrice-formula spec — formula configuration for one contract-line quality attribute. */
export const AdjustedSalesPriceQualitySpecSchema = QualitySpecBaseSchema.extend(
  {
    /** Exactly one unconditional row; see `AdjustedSalesPriceFormulaRowSchema`. */
    formulaRows: z.tuple([AdjustedSalesPriceFormulaRowSchema]),
    /** Discriminant — always `AdjustedSalesPrice` for this variant. */
    formulaType: z.literal(QualityFormulaType.AdjustedSalesPrice),
  },
);

/** QuantityAdjustedToContracted-formula spec — formula configuration for one contract-line quality attribute. */
export const QuantityAdjustedToContractedQualitySpecSchema =
  QualitySpecBaseSchema.extend({
    /** Exactly one unconditional row; see `QuantityFormulaRowSchema`. */
    formulaRows: z.tuple([QuantityFormulaRowSchema]),
    /** Discriminant — always `QuantityAdjustedToContracted` for this variant. */
    formulaType: z.literal(QualityFormulaType.QuantityAdjustedToContracted),
  });

/** QuantityAdjustedToDry-formula spec — formula configuration for one contract-line quality attribute. */
export const QuantityAdjustedToDryQualitySpecSchema =
  QualitySpecBaseSchema.extend({
    /** Exactly one unconditional row with a nonzero adjusted-quantity denominator. */
    formulaRows: z.tuple([
      QuantityFormulaRowSchema.refine(row => row.lowerBound !== 100, {
        message:
          'LowerBound must not be 100 for adjusted dry quantity formulas',
      }),
    ]),
    /** Discriminant — always `QuantityAdjustedToDry` for this variant. */
    formulaType: z.literal(QualityFormulaType.QuantityAdjustedToDry),
  });

/**
 * QuantityAdjustedByPercent-formula spec — formula configuration for one contract-line quality
 * attribute. Unlike every other quantity/single-row variant, it has no `formulaRows` at all:
 * the formula (`shippedQuantity * (actualSpec / 100)`) has no contracted baseline or tier to
 * configure, it only ever reads the measured value itself.
 */
export const QuantityAdjustedByPercentQualitySpecSchema =
  QualitySpecBaseSchema.extend({
    /** Discriminant — always `QuantityAdjustedByPercent` for this variant. */
    formulaType: z.literal(QualityFormulaType.QuantityAdjustedByPercent),
  });

/**
 * Quality specification — formula configuration for one spec type, persisted on a
 * contract line item. `adjustmentType` (Price vs Quantity) is fully determined by
 * `formulaType`, so it's never a separate field here. Percentage/PerUnit variants
 * are themselves further split by `tierMode` (see `PercentageQualitySpecSchema` etc.);
 * AdjustedSalesPrice's single row has its own `contractedSpec`, `QuantityAdjustedToContracted`/
 * `QuantityAdjustedToDry` use their row's `lowerBound` instead, and `QuantityAdjustedByPercent`
 * has no row at all.
 */
export const QualitySpecSchema = z
  .discriminatedUnion('formulaType', [
    PercentageQualitySpecSchema,
    PerUnitQualitySpecSchema,
    AdjustedSalesPriceQualitySpecSchema,
    QuantityAdjustedToContractedQualitySpecSchema,
    QuantityAdjustedToDryQualitySpecSchema,
    QuantityAdjustedByPercentQualitySpecSchema,
  ])
  .superRefine((spec, context) => {
    rejectDisallowedFormulaTypeForSpecType(
      spec.specType,
      spec.formulaType,
      context,
      ['formulaType'],
    );
  });

export type IQualitySpec = z.infer<typeof QualitySpecSchema>;

// ── Calculation endpoint schemas ─────────────────────────────────────────────
// Keyed by SpecType (map/record) rather than array — the calculate endpoint
// takes one request object with quality attributes keyed by their spec type.

const QualityInputBaseSchema = z.object({
  /** The measured value for this spec type — input to the calculation. */
  actualSpec: finiteNumber,
  /** Rounding precision (decimal places) applied to the calculated adjustment. */
  precision: z.number().int().min(0).max(10).default(5),
});

/** Percentage-formula Threshold input — one guaranteed value, 1-2 bound-free rows. */
export const PercentageThresholdQualityInputSchema =
  QualityInputBaseSchema.extend({
    ...buildThresholdVariantShape(
      PercentageThresholdSideSchema,
      QualityFormulaType.Percentage,
    ),
    /** What this input's percentFactor scales against — see ProductPriceBasisSchema. */
    productPriceBasis: ProductPriceBasisSchema.optional(),
  });

/** Percentage-formula Range input — an ordered chain of independently-bounded rows. */
export const PercentageRangeQualityInputSchema = QualityInputBaseSchema.extend({
  ...buildRangeVariantShape(
    PercentageFormulaRowSchema,
    QualityFormulaType.Percentage,
  ),
  /** What this input's percentFactor scales against — see ProductPriceBasisSchema. */
  productPriceBasis: ProductPriceBasisSchema.optional(),
});

/** Percentage-formula input — merges formula configuration and the actual measured value. */
export const PercentageQualityInputSchema = z.discriminatedUnion('tierMode', [
  PercentageThresholdQualityInputSchema,
  PercentageRangeQualityInputSchema,
]);

/** PerUnit-formula Threshold input — one guaranteed value, 1-2 bound-free rows. */
export const PerUnitThresholdQualityInputSchema = QualityInputBaseSchema.extend(
  buildThresholdVariantShape(
    PerUnitThresholdSideSchema,
    QualityFormulaType.PerUnit,
  ),
);

/** PerUnit-formula Range input — an ordered chain of independently-bounded rows. */
export const PerUnitRangeQualityInputSchema = QualityInputBaseSchema.extend(
  buildRangeVariantShape(PerUnitFormulaRowSchema, QualityFormulaType.PerUnit),
);

/** PerUnit-formula input — merges formula configuration and the actual measured value. */
export const PerUnitQualityInputSchema = z.discriminatedUnion('tierMode', [
  PerUnitThresholdQualityInputSchema,
  PerUnitRangeQualityInputSchema,
]);

/** AdjustedSalesPrice-formula input — merges formula configuration and the actual measured value. */
export const AdjustedSalesPriceQualityInputSchema =
  QualityInputBaseSchema.extend({
    /** Exactly one unconditional row; see `AdjustedSalesPriceFormulaRowSchema`. */
    formulaRows: z.tuple([AdjustedSalesPriceFormulaRowSchema]),
    /** Discriminant — always `AdjustedSalesPrice` for this variant. */
    formulaType: z.literal(QualityFormulaType.AdjustedSalesPrice),
  });

/** QuantityAdjustedToContracted-formula input — merges formula configuration and the actual measured value. */
export const QuantityAdjustedToContractedQualityInputSchema =
  QualityInputBaseSchema.extend({
    /** Exactly one unconditional row; see `QuantityFormulaRowSchema`. */
    formulaRows: z.tuple([QuantityFormulaRowSchema]),
    /** Discriminant — always `QuantityAdjustedToContracted` for this variant. */
    formulaType: z.literal(QualityFormulaType.QuantityAdjustedToContracted),
  });

/** QuantityAdjustedToDry-formula input — merges formula configuration and the actual measured value. */
export const QuantityAdjustedToDryQualityInputSchema =
  QualityInputBaseSchema.extend({
    /** Exactly one unconditional row with a nonzero adjusted-quantity denominator. */
    formulaRows: z.tuple([
      QuantityFormulaRowSchema.refine(row => row.lowerBound !== 100, {
        message:
          'lowerBound must not be 100 for adjusted dry quantity formulas',
      }),
    ]),
    /** Discriminant — always `QuantityAdjustedToDry` for this variant. */
    formulaType: z.literal(QualityFormulaType.QuantityAdjustedToDry),
  });

/**
 * QuantityAdjustedByPercent-formula input — merges formula configuration and the actual measured
 * value. No `formulaRows` — see `QuantityAdjustedByPercentQualitySpecSchema`.
 */
export const QuantityAdjustedByPercentQualityInputSchema =
  QualityInputBaseSchema.extend({
    /** Discriminant — always `QuantityAdjustedByPercent` for this variant. */
    formulaType: z.literal(QualityFormulaType.QuantityAdjustedByPercent),
  });

/**
 * Merges formula configuration and the actual measured value for one spec type.
 * `adjustmentType` (Price vs Quantity) is fully determined by `formulaType`, and
 * Percentage/PerUnit are further split by `tierMode` (see `PercentageQualityInputSchema`
 * etc.). `specType` is omitted — it is the key under which this object sits in
 * `qualityInputs`, not a field.
 */
export const QualityInputSchema = z.discriminatedUnion('formulaType', [
  PercentageQualityInputSchema,
  PerUnitQualityInputSchema,
  AdjustedSalesPriceQualityInputSchema,
  QuantityAdjustedToContractedQualityInputSchema,
  QuantityAdjustedToDryQualityInputSchema,
  QuantityAdjustedByPercentQualityInputSchema,
]);

export type IQualityInput = z.infer<typeof QualityInputSchema>;

// Convenience per-variant types — useful for callers building a `qualityInputs`
// entry for a specific, known formula type without the full union.
export type IPercentageQualityInput = z.infer<
  typeof PercentageQualityInputSchema
>;
export type IPerUnitQualityInput = z.infer<typeof PerUnitQualityInputSchema>;

// Per-tierMode convenience types — useful for callers building one specific variant
// (Threshold or Range) without the Threshold/Range union ambiguity above.
export type IPercentageThresholdQualityInput = z.infer<
  typeof PercentageThresholdQualityInputSchema
>;
export type IPercentageRangeQualityInput = z.infer<
  typeof PercentageRangeQualityInputSchema
>;
export type IPerUnitThresholdQualityInput = z.infer<
  typeof PerUnitThresholdQualityInputSchema
>;
export type IPerUnitRangeQualityInput = z.infer<
  typeof PerUnitRangeQualityInputSchema
>;

export type IAdjustedSalesPriceQualityInput = z.infer<
  typeof AdjustedSalesPriceQualityInputSchema
>;
export type IQuantityAdjustedToContractedQualityInput = z.infer<
  typeof QuantityAdjustedToContractedQualityInputSchema
>;
export type IQuantityAdjustedToDryQualityInput = z.infer<
  typeof QuantityAdjustedToDryQualityInputSchema
>;
export type IQuantityAdjustedByPercentQualityInput = z.infer<
  typeof QuantityAdjustedByPercentQualityInputSchema
>;

/**
 * Calculation output for one quality spec. `contractedSpec` is intentionally not
 * echoed here — for Percentage/PerUnit it lives on the request input, and the
 * other formula types never had a separate field for it in the first place. A quantity
 * adjustment's resulting invoice quantity is likewise not echoed per-spec — a shipment has at
 * most one active quantity-type spec (Moisture or Nitrogen, never both — see
 * `CalculateAdjustmentsRequestSchema`'s `superRefine`), so the response's top-level
 * `adjustedInvoiceQuantity` is the only place that value lives.
 */
export const QualityAdjustmentResultSchema = z.object({
  /** The measured value that was evaluated. */
  actualSpec: z.number(),

  /** Whether this spec adjusted the invoice quantity or the unit price — derived from `formulaType`. */
  adjustmentType: z.enum([
    QualityAdjustmentType.Price,
    QualityAdjustmentType.Quantity,
  ]),
  /** Invoice line description, e.g. "Moisture Adjustment (9.25)". */
  description: z.string(),

  /**
   * Identifies, as a 1-based index into the spec's `formulaRows`, which row/side matched
   * `actualSpec`. Null for formula types with a single unconditional row (`AdjustedSalesPrice`
   * and the `Quantity*` variants), where there is no meaningful tier to report. Lets callers
   * such as the UI's quality-formula playground label which configured tier produced a result,
   * without re-deriving the match themselves.
   */
  matchedTierIndex: z.number().int().positive().nullable(),
  /** Non-null for price adjustments — the unit-price delta. */
  priceAdjustmentPerUnit: z.number().nullable(),
  /** Non-null for price adjustments — `priceAdjustmentPerUnit` extended over the response-level `adjustedInvoiceQuantity`. */
  priceAdjustmentTotal: z.number().nullable(),
});

export type IQualityAdjustmentResult = z.infer<
  typeof QualityAdjustmentResultSchema
>;

/** Request body for `POST /quality-adjustments/calculate`. */
export const CalculateAdjustmentsRequestSchema = z
  .object({
    /** Quality attributes keyed by spec type — the full set evaluated in one call. */
    qualityInputs: z.partialRecord(z.enum(SpecType), QualityInputSchema),
    /** The shipment quantity before any quantity adjustment is applied. */
    shippedQuantity: finiteNumber.positive(),
    /** The contract trade price — may be negative (sometimes we are paid to take product from a supplier). */
    tradePrice: finiteNumber,
  })
  .superRefine((request, context) => {
    for (const [specType, input] of Object.entries(request.qualityInputs)) {
      rejectDisallowedFormulaTypeForSpecType(
        specType,
        input.formulaType,
        context,
        ['qualityInputs', specType, 'formulaType'],
      );

      if (input.formulaType === QualityFormulaType.Percentage) {
        rejectZeroContractedSpecForRelativePercentage(
          specType,
          input.contractedSpec,
          context,
          ['qualityInputs', specType, 'contractedSpec'],
        );
      }
    }

    // Confirmed business rule: a shipment has at most one quantity adjustment — Moisture or
    // Nitrogen, never both — so only one quantity-type spec may be active per request.
    const quantitySpecTypes = Object.entries(request.qualityInputs)
      .filter(([, input]) => isQuantityFormulaType(input.formulaType))
      .map(([specType]) => specType);
    if (quantitySpecTypes.length > 1) {
      context.addIssue({
        code: 'custom',
        message: `Only one quantity-adjustment spec may be active at a time, found: ${quantitySpecTypes.join(', ')}`,
        path: ['qualityInputs'],
      });
    }
  });

export type ICalculateAdjustmentsRequest = z.infer<
  typeof CalculateAdjustmentsRequestSchema
>;

/** Response body for `POST /quality-adjustments/calculate`. */
export const CalculateAdjustmentsResponseSchema = z.object({
  /** `shippedQuantity` after applying any quantity-type adjustment (equal to `shippedQuantity` if none). */
  adjustedInvoiceQuantity: z.number(),
  /** Calculation results keyed by spec type. */
  adjustments: z.partialRecord(z.enum(SpecType), QualityAdjustmentResultSchema),
  /** Echoes the request's `shippedQuantity` unchanged. */
  shippedQuantity: z.number(),
  /** Echoes the request's `tradePrice` unchanged. */
  tradePrice: z.number(),
});

export type ICalculateAdjustmentsResponse = z.infer<
  typeof CalculateAdjustmentsResponseSchema
>;
